import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { usePlayer, fmtTime } from "../context/PlayerContext";
import { Play, Pause, ListPlus, ArrowLeft, Users, BookOpen, Clock, FileText, User, Globe, X, Download } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";

export default function SeriesDetail() {
  const { name } = useParams();
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const { playTracks, addToQueue, current, isPlaying, toggle } = usePlayer();
  const [readerOpen, setReaderOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const [d, m] = await Promise.all([
          api.seriesDetail(name),
          api.manifest().catch(() => null),
        ]);
        if (!alive) return;
        setData(d);
        if (m?.series) {
          const match = m.series.find(s => s.name?.toLowerCase() === name.toLowerCase());
          if (match) setMeta(match);
        }
      } catch (e) { toast.error("Failed to load series"); }
      finally { alive && setLoading(false); }
    })();
    return () => { alive = false; };
  }, [name]);

  const playFromItem = async (idx) => {
    if (!data?.items?.length) return;
    try {
      const target = data.items[idx];
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/item/${encodeURIComponent(target.id)}`);
      const detail = await res.json();
      if (!detail.tracks?.length) { toast.error("No playable audio"); return; }
      const tracks = detail.tracks.map(t => ({
        id: `${detail.id}::${t.name}`,
        title: detail.title,
        creator: detail.creator,
        thumbnail: detail.thumbnail,
        url: t.url,
        length: t.length,
        parent: detail.id,
        parentTitle: detail.title,
      }));
      playTracks(tracks, 0);
    } catch { toast.error("Failed to start playback"); }
  };

  if (loading) return (
    <div className="animate-pulse space-y-6" data-testid="series-loading">
      <div className="h-8 w-40 bg-white/5 rounded" />
      <div className="h-40 bg-white/[0.03] rounded-lg" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-24 bg-white/[0.03] rounded-lg" />)}
      </div>
    </div>
  );

  if (!data || !data.count) return (
    <div className="archival-card rounded-lg p-12 text-center" data-testid="series-empty">
      <p className="font-display text-2xl">Series not found</p>
      <Link to="/collections" className="btn-ghost-gold inline-flex mt-4 items-center gap-2">
        <ArrowLeft className="w-4 h-4" /> Back to Collections
      </Link>
    </div>
  );

  const totalPlays = data.total_downloads || 0;

  return (
    <div className="animate-fade-up" data-testid="page-series">
      <Link to="/collections" className="inline-flex items-center gap-2 text-white/60 hover:text-emerald-soft mb-6" data-testid="series-back">
        <ArrowLeft className="w-4 h-4" /> Back to Collections
      </Link>

      <section className="relative overflow-hidden rounded-xl border border-white/8 bg-gradient-to-br from-[#1a1108] via-[#161009] to-[#0F0B08] px-5 sm:px-6 lg:px-10 py-8 sm:py-10 lg:py-12">
        <div className="absolute -top-16 -right-16 w-[380px] h-[380px] rounded-full bg-emerald-glow/[0.08] blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[260px] h-[260px] rounded-full bg-gold/[0.06] blur-3xl" />
        <div className="relative grid md:grid-cols-[200px_1fr] gap-6 sm:gap-8 items-start">
          <div className="w-[140px] sm:w-full max-w-[200px] aspect-square rounded-lg overflow-hidden border border-white/8 bg-black/40 mx-auto md:mx-0">
            {data.thumbnail && (
              <img
                src={data.thumbnail}
                alt=""
                className={`w-full h-full ${meta?.cover_fit === "contain" ? "object-contain" : "object-cover"}`}
              />
            )}
          </div>
          <div>
            <p className="label-caps text-gold/85 mb-2">Series · {meta?.discipline ? "Aqeedah & Manhaj" : "Lecture Series"}</p>
            <h1 className="font-display text-2xl sm:text-3xl md:text-5xl leading-tight text-paper" data-testid="series-title">{data.name}</h1>
            {meta?.translation && (
              <p className="text-lg text-gold/80 italic mt-2">{meta.translation}</p>
            )}
            {data.creator && <p className="text-lg text-white/70 mt-3">{data.creator}</p>}
            <div className="mt-5 flex flex-wrap items-center gap-5 text-sm text-white/60">
              <span className="inline-flex items-center gap-1.5"><BookOpen className="w-3.5 h-3.5 text-emerald-soft" /> {data.count} lectures</span>
              <span className="inline-flex items-center gap-1.5"><Users className="w-3.5 h-3.5 text-gold" /> {totalPlays.toLocaleString()} plays</span>
              {meta?.language && (
                <span className="inline-flex items-center gap-1.5"><Globe className="w-3.5 h-3.5 text-emerald-soft" /> {meta.language}</span>
              )}
              {meta?.year_started && (
                <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-gold" /> Started {meta.year_started}</span>
              )}
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <button onClick={() => playFromItem(0)} className="btn-primary inline-flex items-center gap-2" data-testid="series-play-all">
                <Play className="w-4 h-4 fill-current" /> Play from start
              </button>
              {meta?.pdf_url && (
                <>
                  <button
                    onClick={() => setReaderOpen(true)}
                    className="btn-ghost-gold inline-flex items-center gap-2"
                    data-testid="series-open-reader"
                  >
                    <BookOpen className="w-4 h-4" /> Read the book
                  </button>
                  <a
                    href={meta.pdf_url}
                    target="_blank"
                    rel="noreferrer"
                    className="chip"
                    data-testid="series-pdf-download"
                    title="Download PDF"
                  >
                    <Download className="w-3.5 h-3.5" /> PDF
                  </a>
                </>
              )}
              <Link to={`/explore?series=${encodeURIComponent(data.name)}`} className="chip" data-testid="series-open-in-explore">
                Open in Explore
              </Link>
            </div>
            {meta?.author && (
              <div className="mt-5 flex flex-wrap gap-x-5 gap-y-1 text-xs text-white/55">
                <span className="inline-flex items-center gap-1.5"><BookOpen className="w-3 h-3 text-gold" /> Musannif: {meta.author}</span>
                {meta.urdu_by && (
                  <span className="inline-flex items-center gap-1.5"><User className="w-3 h-3 text-emerald-soft" /> Urdu tarjuma: {meta.urdu_by}</span>
                )}
                {meta.publisher && (
                  <span className="inline-flex items-center gap-1.5">Naashir: {meta.publisher}</span>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {meta?.book_description && (
        <section className="mt-10" data-testid="series-book-description">
          <div className="archival-card featured rounded-lg p-6 lg:p-8 relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-gold/[0.05] blur-3xl" />
            <div className="relative">
              <div className="flex items-center gap-2 mb-3">
                <FileText className="w-4 h-4 text-gold" />
                <span className="label-caps text-gold/90">About the Book</span>
              </div>
              <p className="text-white/80 text-[17px] leading-[1.8]">{meta.book_description}</p>
              {meta.pdf_url && (
                <div className="mt-5 flex flex-wrap items-center gap-3">
                  <button
                    onClick={() => setReaderOpen(true)}
                    className="btn-ghost-gold inline-flex items-center gap-2 text-sm"
                    data-testid="series-book-open-reader"
                  >
                    <BookOpen className="w-4 h-4" /> Read in browser
                  </button>
                  <a href={meta.pdf_url} target="_blank" rel="noreferrer" className="chip" data-testid="series-book-pdf">
                    <Download className="w-3.5 h-3.5" /> Download PDF
                  </a>
                  {meta.pdf_archive_url && (
                    <a href={meta.pdf_archive_url} target="_blank" rel="noreferrer" className="chip" data-testid="series-book-archive">
                      Archive.org page
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {meta?.description && (
        <section className="mt-8 max-w-3xl">
          <p className="label-caps text-gold/85 mb-2">About this series</p>
          <p className="text-white/75 leading-relaxed">{meta.description}</p>
        </section>
      )}

      <section className="mt-10">
        <p className="label-caps text-gold/85">Lessons</p>
        <h2 className="font-display text-2xl text-paper mt-1 mb-6">Full listing</h2>
        <div className="archival-card rounded-lg overflow-hidden" data-testid="series-lesson-list">
          {data.items.map((it, i) => {
            const nowPlaying = current?.parent === it.id;
            return (
              <div
                key={it.id}
                className={`flex items-center gap-3 md:gap-4 px-3 md:px-4 py-3 border-b border-white/5 last:border-0 hover:bg-white/[0.02] transition ${nowPlaying ? "bg-emerald-glow/10" : ""}`}
                data-testid={`series-row-${i}`}
              >
                <button
                  onClick={() => (nowPlaying ? toggle() : playFromItem(i))}
                  className="play-btn shrink-0 !w-10 !h-10"
                  aria-label={nowPlaying && isPlaying ? "Pause" : "Play"}
                  data-testid={`series-row-play-${i}`}
                >
                  {nowPlaying && isPlaying
                    ? <Pause className="w-4 h-4 fill-current" />
                    : <Play className="w-4 h-4 ml-0.5 fill-current" />}
                </button>
                <div className="hidden sm:block w-10 text-white/40 tabular-nums font-display text-sm shrink-0">{String(i + 1).padStart(2, "0")}</div>
                <Link to={`/item/${encodeURIComponent(it.id)}`} className="flex-1 min-w-0 group">
                  <div className={`truncate font-display text-[15px] md:text-[17px] ${nowPlaying ? "text-emerald-soft" : "text-paper group-hover:text-emerald-soft"}`}>
                    {it.title}
                  </div>
                  {it.date && (
                    <div className="text-xs text-white/40 mt-0.5 inline-flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {String(it.date).slice(0, 10)}
                    </div>
                  )}
                </Link>
                <button
                  onClick={async () => {
                    try {
                      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/item/${encodeURIComponent(it.id)}`);
                      const d = await res.json();
                      const t = d.tracks?.[0];
                      if (t) addToQueue({
                        id: `${d.id}::${t.name}`, title: d.title, creator: d.creator,
                        thumbnail: d.thumbnail, url: t.url, length: t.length,
                        parent: d.id, parentTitle: d.title,
                      });
                    } catch { toast.error("Failed to queue"); }
                  }}
                  className="chip shrink-0"
                  aria-label="Add to queue"
                  data-testid={`series-row-queue-${i}`}
                >
                  <ListPlus className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Queue</span>
                </button>
              </div>
            );
          })}
        </div>
      </section>

      {meta?.pdf_url && (
        <Dialog open={readerOpen} onOpenChange={setReaderOpen}>
          <DialogContent
            className="max-w-6xl w-[95vw] h-[92vh] p-0 bg-[#0F0B08] border border-white/10 text-paper overflow-hidden flex flex-col"
            data-testid="book-reader-dialog"
          >
            <DialogTitle className="sr-only">Book reader — {data.name}</DialogTitle>
            <div className="flex items-center justify-between gap-4 px-5 py-3 border-b border-white/5">
              <div className="min-w-0">
                <p className="label-caps text-gold/85">Book Reader · listening continues</p>
                <p className="font-display text-lg text-paper truncate">{data.name}</p>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={meta.pdf_url}
                  target="_blank"
                  rel="noreferrer"
                  className="chip"
                  data-testid="book-reader-download"
                >
                  <Download className="w-3.5 h-3.5" /> Download
                </a>
                <button
                  onClick={() => setReaderOpen(false)}
                  className="p-2 text-white/70 hover:text-paper rounded-full hover:bg-white/5"
                  aria-label="Close"
                  data-testid="book-reader-close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="flex-1 bg-black">
              <iframe
                src={meta.pdf_url}
                title={data.name}
                className="w-full h-full border-0"
                data-testid="book-reader-iframe"
              />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
